import { BotMessageSquare, Clipboard, FileText, Loader2, RotateCcw, Send, ShieldCheck, UserRound } from "lucide-react";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { createParentReport, streamChat } from "./api";
import type { ChatMessage, ChatMeta, ParentInsight } from "./types";

type UiMessage = ChatMessage & {
  kind?: "normal" | "report";
};

const STORAGE_KEY = "parent-consultation-chat-v2";

const starterMessage: UiMessage = {
  id: "welcome",
  sender: "ai",
  role: "parent",
  kind: "normal",
  content:
    "我们可以先从一个具体变化开始。\n你最近最担心孩子哪一点：情绪、行为、学习，还是亲子冲突？\n如果方便，也告诉我孩子大概年龄或年级。",
  createdAt: new Date().toISOString(),
  riskLevel: "low",
  emotion: "neutral"
};

const quickStarts = ["孩子一写作业就崩溃", "孩子不想上学", "孩子总说自己很差", "孩子注意力很难集中"];

function loadMessages() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? (JSON.parse(saved) as UiMessage[]) : [starterMessage];
  } catch {
    return [starterMessage];
  }
}

function makeId(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function formatTime(value: string) {
  return new Intl.DateTimeFormat("zh-CN", {
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(value));
}

function buildConversationMemory(messages: UiMessage[]) {
  const parentMessages = messages
    .filter((message) => message.sender === "user" && message.kind !== "report")
    .map((message) => message.content.replace(/\s+/g, " ").trim())
    .filter(Boolean);

  if (!parentMessages.length) return "";

  const lines = parentMessages.map((content, index) => `${index + 1}. ${content}`);
  return [
    "家长已提供的信息，后续回复必须记住，不要让家长重复提供：",
    ...lines,
    "如果这些信息里已经包含孩子年龄、年级、持续时间、触发场景、家长回应方式，请直接沿用。"
  ].join("\n");
}

function formatReport(report: ParentInsight) {
  const suggestions = report.parent_response_suggestions.length
    ? report.parent_response_suggestions
    : ["先继续补充孩子年龄、场景、持续时间和家长回应方式。"];
  const plan = report.family_action_plan.length ? report.family_action_plan : ["接下来 3 天记录触发场景、孩子反应和家长回应。"];
  const helpIndicators = report.professional_help_indicators.length
    ? report.professional_help_indicators
    : ["如果孩子出现自伤/自杀表达、被伤害风险、严重校园暴力、持续失眠进食明显受影响，或家庭已经无法保证安全，请尽快联系现实中的专业人员或当地紧急服务。"];

  return [
    "父母咨询总结与家庭支持行动文件",
    "",
    "一、安全风险",
    report.safety_risk,
    "",
    "二、核心问题",
    report.core_problem,
    "",
    "三、ABC 分析",
    `A 触发事件：${report.abc_analysis.antecedent}`,
    `B 可能想法：${report.abc_analysis.belief_or_thought}`,
    `C 情绪/行为结果：${report.abc_analysis.consequence}`,
    "",
    "四、5P 个案理解",
    `1. 当前问题：${report.case_formulation_5p.presenting_problem}`,
    `2. 易感因素：${report.case_formulation_5p.predisposing_factors}`,
    `3. 触发因素：${report.case_formulation_5p.precipitating_factors}`,
    `4. 维持因素：${report.case_formulation_5p.perpetuating_factors}`,
    `5. 保护因素：${report.case_formulation_5p.protective_factors}`,
    "",
    "五、亲子互动循环",
    report.parent_child_interaction_loop,
    "",
    "六、父母回应建议",
    ...suggestions.map((item, index) => `${index + 1}. ${item}`),
    "",
    "七、3-7 天家庭行动计划",
    ...plan.map((item, index) => `Day ${index + 1}: ${item}`),
    "",
    "八、何时需要真人专业帮助",
    ...helpIndicators.map((item) => `- ${item}`),
    "",
    `说明：${report.limitations}`
  ].join("\n");
}

export function App() {
  const [messages, setMessages] = useState<UiMessage[]>(loadMessages);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [lastMeta, setLastMeta] = useState<ChatMeta | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const userMessageCount = useMemo(() => messages.filter((message) => message.sender === "user").length, [messages]);
  const lastReport = useMemo(() => [...messages].reverse().find((message) => message.kind === "report"), [messages]);
  const currentRisk = lastMeta?.risk_level ?? messages[messages.length - 1]?.riskLevel ?? "low";
  const sessionTitle = currentRisk === "high" ? "安全优先" : userMessageCount ? "咨询进行中" : "准备开始";
  const sessionSubtitle =
    currentRisk === "high" ? "先确认现实安全" : userMessageCount ? "正在形成家庭支持思路" : "从一个具体变化开始";

  const appendUserAndStream = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isStreaming || isGeneratingReport) return;

    const userMessage: UiMessage = {
      id: makeId("user"),
      sender: "user",
      role: "parent",
      kind: "normal",
      content: trimmed,
      createdAt: new Date().toISOString()
    };
    const aiMessage: UiMessage = {
      id: makeId("ai"),
      sender: "ai",
      role: "parent",
      kind: "normal",
      content: "",
      createdAt: new Date().toISOString()
    };
    const context = [...messages, userMessage];
    const conversationMemory = buildConversationMemory(context);

    setInput("");
    setIsStreaming(true);
    setMessages((items) => [...items, userMessage, aiMessage]);

    let streamedText = "";
    let meta: ChatMeta | null = null;

    try {
      await streamChat(
        {
          role: "parent",
          message: trimmed,
          childProfile: {
            nickname: "",
            ageRange: "",
            schoolStage: "",
            strengths: [],
            knownTriggers: [],
            supportContacts: []
          },
          recentMessages: messages.filter((message) => message.kind !== "report"),
          memorySummary: conversationMemory
        },
        {
          onMeta: (nextMeta) => {
            meta = nextMeta;
            setLastMeta(nextMeta);
          },
          onToken: (token) => {
            streamedText += token;
            setMessages((items) =>
              items.map((item) => (item.id === aiMessage.id ? { ...item, content: streamedText } : item))
            );
          },
          onDone: (doneMeta) => {
            if (doneMeta) meta = doneMeta;
          }
        }
      );

      const finalMeta = meta as ChatMeta | null;
      setMessages((items) =>
        items.map((item) =>
          item.id === aiMessage.id
            ? {
                ...item,
                content: streamedText || "我在。我们可以先把孩子最近最明显的变化说清楚一点。",
                riskLevel: finalMeta?.risk_level,
                emotion: finalMeta?.structured_analysis.emotion_detected
              }
            : item
        )
      );
    } catch {
      setMessages((items) =>
        items.map((item) =>
          item.id === aiMessage.id
            ? {
                ...item,
                content: "AI 服务暂时不稳定。\n你可以先补充：孩子多大、这个变化持续多久、最常发生在哪个场景。",
                riskLevel: "medium",
                emotion: "unclear"
              }
            : item
        )
      );
    } finally {
      setIsStreaming(false);
    }
  };

  const sendMessage = async (event: FormEvent) => {
    event.preventDefault();
    await appendUserAndStream(input);
  };

  const generateReport = async () => {
    if (isStreaming || isGeneratingReport) return;

    if (userMessageCount < 2) {
      await appendUserAndStream("我想整理咨询总结，但目前信息可能还不够。请先引导我补充关键情况。");
      return;
    }

    const pendingMessage: UiMessage = {
      id: makeId("report"),
      sender: "ai",
      role: "parent",
      kind: "report",
      content: "正在整理本次咨询总结与 7 天家庭支持计划...",
      createdAt: new Date().toISOString()
    };

    setIsGeneratingReport(true);
    setMessages((items) => [...items, pendingMessage]);

    try {
      const report = await createParentReport({
        messages,
        emotionEvents: [],
        childProfile: {}
      });

      setMessages((items) =>
        items.map((item) =>
          item.id === pendingMessage.id
            ? {
                ...item,
                content: formatReport(report)
              }
            : item
        )
      );
    } catch {
      setMessages((items) =>
        items.map((item) =>
          item.id === pendingMessage.id
            ? {
                ...item,
                content: "报告生成暂时失败。\n可以继续对话补充情况，稍后再生成咨询总结。"
              }
            : item
        )
      );
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const resetConversation = () => {
    setMessages([{ ...starterMessage, createdAt: new Date().toISOString() }]);
    setLastMeta(null);
    setInput("");
  };

  const copyReport = async () => {
    if (!lastReport) return;
    await navigator.clipboard.writeText(lastReport.content);
  };

  return (
    <main className="consult-shell">
      <section className="consult-window">
        <header className="consult-header">
          <div className="brand-lockup">
            <div className="brand-symbol">
              <ShieldCheck size={21} />
            </div>
            <div>
              <span className="eyebrow">儿童与家庭心理支持</span>
              <h1>Family Mind AI</h1>
              <p>父母咨询引导</p>
            </div>
          </div>
          <div className="header-actions">
            <div className={`session-state risk-${currentRisk}`} aria-label={sessionTitle}>
              <span className="status-dot" />
              <div>
                <strong>{sessionTitle}</strong>
                <span>{sessionSubtitle}</span>
              </div>
            </div>
            <button className="icon-button" onClick={resetConversation} title="重新开始" aria-label="重新开始">
              <RotateCcw size={17} />
            </button>
          </div>
        </header>

        <div className="message-stream" ref={listRef}>
          <div className="session-marker">今天的咨询</div>
          {messages.map((message) => (
            <article
              key={message.id}
              className={`message-row ${message.sender === "user" ? "message-user" : "message-ai"} ${message.kind === "report" ? "message-report" : ""}`}
            >
              <div className="message-avatar" aria-hidden="true">
                {message.sender === "user" ? (
                  <UserRound size={17} />
                ) : message.kind === "report" ? (
                  <FileText size={17} />
                ) : (
                  <BotMessageSquare size={17} />
                )}
              </div>
              <div className={`bubble ${message.sender === "user" ? "bubble-user" : "bubble-ai"} ${message.kind === "report" ? "bubble-report" : ""}`}>
                <div className="bubble-meta">
                  <span>{message.sender === "user" ? "你" : message.kind === "report" ? "咨询总结" : "AI 咨询引导"}</span>
                  <time>{formatTime(message.createdAt)}</time>
                </div>
                <p>{message.content || "正在回应..."}</p>
              </div>
            </article>
          ))}
        </div>

        {userMessageCount === 0 && (
          <div className="quick-starts" aria-label="常见咨询入口">
            {quickStarts.map((item) => (
              <button key={item} onClick={() => appendUserAndStream(item)} disabled={isStreaming}>
                {item}
              </button>
            ))}
          </div>
        )}

        <footer className="consult-footer">
          {currentRisk === "high" && (
            <div className="safe-banner">
              已进入安全优先模式。请先确认孩子当前位置、是否有人陪伴，以及是否需要现实中的紧急帮助。
            </div>
          )}
          <form className="composer" onSubmit={sendMessage}>
            <div className="composer-field">
              <textarea
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    void appendUserAndStream(input);
                  }
                }}
                placeholder="描述孩子最近的变化，例如：年龄、表现、持续多久、通常发生在哪个场景"
                rows={3}
              />
            </div>
            <div className={`composer-actions ${lastReport ? "has-report" : ""}`}>
              <button className="secondary-button" type="button" onClick={generateReport} disabled={isStreaming || isGeneratingReport}>
                {isGeneratingReport ? <Loader2 className="spin" size={17} /> : <FileText size={17} />}
                生成总结
              </button>
              {lastReport && (
                <button className="secondary-button" type="button" onClick={copyReport}>
                  <Clipboard size={17} />
                  复制总结
                </button>
              )}
              <button className="send-button" type="submit" disabled={isStreaming || isGeneratingReport || !input.trim()}>
                {isStreaming ? <Loader2 className="spin" size={18} /> : <Send size={18} />}
                发送
              </button>
            </div>
          </form>
          <p className="fine-print">
            这里提供非诊断性的家庭心理支持。若孩子存在自伤、被伤害、严重崩溃或持续功能受损，请及时联系现实中的专业人员或紧急支持。
          </p>
        </footer>
      </section>
    </main>
  );
}
